<script setup lang="ts">
/**
 * `FrameRateStatsView.vue` — DevTools-enabled Stats.js frame-rate overlay.
 *
 * Web-only diagnostic. It follows Campfire's renderer-owned `FrameModel`
 * instead of starting a second animation loop, so the reading covers the
 * frames the app actually renders (including WebXR's session frame clock).
 * It is off by default and can be toggled from DevTools with
 * `__campfireFrameStats(true|false)`; the preference survives reloads.
 */
import { onMounted, onUnmounted, ref } from "vue";
import Stats from "stats.js";
import { FRAME_MODEL, type FrameModel } from "@core/frame/frame";
import { Ioc } from "@core/ioc/ioc";

defineOptions({ name: "FrameRateStatsView" });

const K_PREF_KEY = "Campfire.Dev.FrameRateStats";

declare global {
  interface Window {
    __campfireFrameStats?: (enabled?: boolean) => boolean;
  }
}

const host = ref<HTMLElement | null>(null);
const isEnabled = ref(readEnabledPref());
let offFrame: (() => void) | null = null;
let stats: Stats | null = null;

function start(): void {
  if (stats) return;
  stats = new Stats();
  stats.showPanel(0);
  stats.dom.style.position = "relative";
  stats.dom.style.top = "auto";
  stats.dom.style.left = "auto";
  host.value?.appendChild(stats.dom);

  offFrame = Ioc.resolve<FrameModel>(FRAME_MODEL).onLateTick.on(() => {
    stats?.update();
  });
}

function stop(): void {
  offFrame?.();
  offFrame = null;
  stats?.dom.remove();
  stats = null;
}

function setEnabled(enabled: boolean): boolean {
  isEnabled.value = enabled;
  writeEnabledPref(enabled);
  if (enabled) start();
  else stop();
  return enabled;
}

const toggleFromDevTools = (enabled?: boolean): boolean =>
  setEnabled(enabled ?? !isEnabled.value);

onMounted(() => {
  window.__campfireFrameStats = toggleFromDevTools;
  if (isEnabled.value) start();
});

onUnmounted(() => {
  if (window.__campfireFrameStats === toggleFromDevTools) {
    delete window.__campfireFrameStats;
  }
  stop();
});

function readEnabledPref(): boolean {
  try {
    return window.localStorage.getItem(K_PREF_KEY) === "1";
  } catch {
    return false;
  }
}

function writeEnabledPref(enabled: boolean): void {
  try {
    if (enabled) window.localStorage.setItem(K_PREF_KEY, "1");
    else window.localStorage.removeItem(K_PREF_KEY);
  } catch {
    /* private-mode storage refusal leaves the current session toggle usable */
  }
}
</script>

<template>
  <aside
    v-show="isEnabled"
    ref="host"
    class="cf-frame-stats"
    aria-label="Frame rate statistics"
  />
</template>

<style scoped>
.cf-frame-stats {
  position: fixed;
  top: max(var(--cf-space-2), env(safe-area-inset-top));
  left: max(var(--cf-space-2), env(safe-area-inset-left));
  z-index: 1001;
  width: 80px;
  height: 48px;
  pointer-events: auto;
}
</style>
